import { Controller } from "stimulus"
import { DirectUpload } from "@rails/activestorage"
import * as FilePond from "filepond"
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size"
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type"

FilePond.registerPlugin(FilePondPluginFileValidateSize, FilePondPluginFileValidateType)

const { FileStatus } = FilePond
const BUSY_STATUSES = [FileStatus.INIT, FileStatus.LOADING, FileStatus.PROCESSING_QUEUED, FileStatus.PROCESSING, FileStatus.IDLE]
const FAILED_STATUSES = [FileStatus.LOAD_ERROR, FileStatus.PROCESSING_ERROR]

// ActiveStorage's DirectUpload reports failures as text: `Error reading "<name>"`,
// `Error creating Blob for "<name>". Status: <n>` (our server) or `Error storing "<name>". Status: <n>` (S3).
// Status 0 means the request never got an answer (network cut, tab put in the background).
function uploadStatus(message) {
  const match = /Status: (\d+)/.exec(message)
  return match ? Number(match[1]) : null
}

// Which DirectUpload step failed. Only this and the status are reported: the message itself
// carries the merchant's filename, which can hold personal data.
function uploadStage(message) {
  if (message.startsWith("Error reading")) return "reading"
  if (message.startsWith("Error creating Blob")) return "creating"
  if (message.startsWith("Error storing")) return "storing"
  return null
}

function uploadErrorLabel(message) {
  const text = String(message || "")
  if (text.startsWith("Error reading")) return "Fichier illisible, choisissez-le à nouveau"

  const status = uploadStatus(text)
  if (status === 0) return "Connexion interrompue, vérifiez votre réseau"
  // The direct upload endpoint checks the CSRF token: 422 means the page is too old.
  if (status === 422 && text.startsWith("Error creating Blob")) return "Session expirée, rechargez la page"
  return "Échec de l'envoi"
}

// KYC document uploader: each file is sent to storage as soon as it is picked
// (ActiveStorage direct upload), so the form only carries its signed id. Files over
// 5 Mo or that are not a PDF/image are refused before any upload.
// Usage: data-controller="kycUpload" around a file input (data-kycUpload-target="input").
// The input's `required` and `multiple` attributes drive FilePond's; files already
// stored are listed by the view and counted in data-kycUpload-attached-count-value.
export default class extends Controller {
  static targets = ["input"]
  static values = { directUploadUrl: String, errorReportUrl: String, attachedCount: Number }

  connect() {
    const input = this.inputTarget
    // conditionalCheckbox may already have disabled the input of a hidden field. FilePond reads the
    // input's attributes over its options and would stay locked once the field is revealed, so the
    // attribute is lifted here and re-applied by syncDisabled() to FilePond's own input, which
    // conditionalCheckbox keeps toggling.
    this.disabledAtConnect = input.disabled
    input.disabled = false
    // FilePond takes the input out of the page, so its name is kept for the error reports.
    this.fieldName = input.name
    this.pond = FilePond.create(input, {
      // Passed explicitly: FilePond does not read Rails' required="required" as true.
      name: input.name,
      required: input.required,
      allowMultiple: input.multiple,
      credits: false,
      storeAsFile: false,
      maxFileSize: "5MB",
      acceptedFileTypes: ["image/*", "application/pdf"],
      server: { process: this.process.bind(this), revert: null, restore: null, load: null, fetch: null },
      labelIdle: 'Glissez votre fichier ou <span class="filepond--label-action">parcourez</span>',
      labelFileProcessing: "Envoi en cours",
      labelFileProcessingComplete: "Fichier envoyé",
      labelFileProcessingError: (error) => uploadErrorLabel(error && error.body),
      labelTapToCancel: "appuyez pour annuler",
      labelTapToRetry: "appuyez pour réessayer",
      labelTapToUndo: "appuyez pour retirer",
      labelButtonRemoveItem: "Retirer",
      labelButtonRetryItemProcessing: "Réessayer",
      labelButtonAbortItemProcessing: "Annuler",
      labelInvalidField: "Ce champ contient un fichier invalide",
      labelMaxFileSizeExceeded: "Fichier trop lourd",
      labelMaxFileSize: "Taille maximale : {filesize}",
      labelFileTypeNotAllowed: "Format non accepté",
      fileValidateTypeLabelExpectedTypes: "PDF ou image uniquement",
      onprocessfile: () => this.notifyChange(),
      onremovefile: () => this.notifyChange(),
      // FilePond builds its browse input on its first render, after create() has returned.
      oninit: () => this.syncDisabled()
    })
    this.syncRequired()
  }

  // A document asked only for some answers is disabled while its field is hidden: a required
  // input the merchant cannot see would make the browser silently refuse the final submit.
  // Outside such a field, the input keeps the disabled state it was rendered with.
  syncDisabled() {
    const browseInput = this.element.querySelector("input.filepond--browser")
    if (!browseInput) return

    const answerField = this.element.closest('[data-conditionalCheckbox-target="field"]')
    browseInput.disabled = answerField ? answerField.classList.contains("is-hidden") : this.disabledAtConnect
  }

  // For a document mandatory only for some answers, conditionalCheckbox stores the current
  // requirement in data-conditionally-required (it may run before this controller connects).
  syncRequired() {
    if (!this.pond || !("conditionallyRequired" in this.element.dataset)) return

    this.pond.required = this.element.dataset.conditionallyRequired === "true" && this.attachedCountValue === 0
  }

  disconnect() {
    if (this.pond) this.pond.destroy()
    this.pond = null
  }

  // FilePond custom processing: hand the file to ActiveStorage and report its signed id.
  process(_fieldName, file, _metadata, load, error, progress, abort) {
    const upload = new DirectUpload(file, this.directUploadUrlValue, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => progress(event.lengthComputable, event.loaded, event.total))
      }
    })

    upload.create((uploadError, blob) => {
      if (uploadError) {
        this.reportUploadError(String(uploadError), file)
        error(String(uploadError))
      } else {
        load(blob.signed_id)
      }
    })

    return { abort: () => abort() }
  }

  // Sends the failure to Sentry through the server. Best effort: a report that fails
  // (often for the same network reason as the upload) is dropped without bothering the merchant.
  reportUploadError(message, file) {
    if (!this.hasErrorReportUrlValue) return

    fetch(this.errorReportUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        stage: uploadStage(message),
        status: uploadStatus(message),
        field: this.fieldName,
        content_type: file.type,
        byte_size: file.size
      })
    }).catch(() => {})
  }

  // Lets the wizard clear an error shown on this uploader once the file is fixed.
  notifyChange() {
    this.element.dispatchEvent(new CustomEvent("kycUpload:change", { bubbles: true }))
  }

  // The three methods below are read by the KYC wizard before leaving a step.
  isBusy() {
    return this.items().some((item) => BUSY_STATUSES.includes(item.status))
  }

  // A refused (size/type) or failed upload leaves no file behind: it must be retried or removed.
  hasFailedFiles() {
    return this.items().some((item) => FAILED_STATUSES.includes(item.status))
  }

  // Files already stored plus the ones uploaded in this session.
  fileCount() {
    return this.attachedCountValue + this.items().filter((item) => item.status === FilePond.FileStatus.PROCESSING_COMPLETE).length
  }

  items() {
    return this.pond ? this.pond.getFiles() : []
  }
}
